"use client"

import { Play, Pause } from "lucide-react"
import { type HubPreviewSound, type HubPreviewState } from "@/hooks/useHubPreview"

interface HubPreviewButtonProps extends Pick<HubPreviewState, "isPlaying" | "activeIndex" | "isLoading"> {
  previewSounds: HubPreviewSound[]
  /**
   * A resolved CSS color, not a token name. Collection accents are `--tls-c-*`
   * RGB triplets and category accents are `--accent-category-*` oklch() colors,
   * so the caller resolves its own and this component stays shape-agnostic.
   */
  accentColor: string
  onToggle: () => void
}

/**
 * The "hear the first few" CTA in a hub header. Shared by collection and
 * category hubs so both give a cold visitor something to play in the first
 * viewport rather than a title and a grid.
 */
export default function HubPreviewButton({
  previewSounds,
  accentColor,
  isPlaying,
  activeIndex,
  isLoading,
  onToggle,
}: HubPreviewButtonProps) {
  const previewCount = Math.min(3, previewSounds.length)
  const label = isPlaying ? "Stop preview" : `Play preview of ${previewCount}`
  const accentEdge = `color-mix(in srgb, var(--tls-brand-fg) 42%, transparent)`

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={previewSounds.length === 0}
      className="group inline-flex h-12 items-center gap-3 rounded-full px-5 text-sm font-medium text-[var(--tls-brand-fg)] transition-[opacity,translate] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)] motion-safe:active:translate-y-px disabled:opacity-50 disabled:pointer-events-none"
      // Fill and foreground are one decision, so they are read from one
      // pair. `accentColor` used to set this, and the two hubs passed
      // different values — the category hub the accent, the collection hub
      // the lift — so whichever foreground was chosen failed AA on the other
      // (2.51:1 and 3.12:1, axe TES-1926). Every --accent-category-* resolves
      // to the one accent on this branch anyway, so the prop bought variance
      // without buying colour.
      style={{ background: "var(--tls-brand)", boxShadow: `0 1px 0 0 ${accentEdge} inset` }}
      aria-label={label}
    >
      <span
        aria-hidden
        className="flex size-7 items-center justify-center rounded-full"
        style={{ background: "color-mix(in srgb, var(--tls-brand-fg) 16%, transparent)" }}
      >
        {isPlaying ? (
          <Pause className="size-3.5" fill="currentColor" />
        ) : (
          <Play className="size-3.5 translate-x-[1px]" fill="currentColor" />
        )}
      </span>
      <span>{isPlaying ? "Stop preview" : "Play preview"}</span>
      {/* Real content (it tells the user how many sounds the preview will
          play), so WCAG 1.4.3 applies. It inherits --tls-canvas from the
          button at full strength: the on-accent ink only clears AA by a hair
          on the tightest accent, so there is no headroom for an opacity
          multiplier. `opacity-75` here measured 3.55:1 (TES-1580). Dim it
          typographically instead, via size and weight.

          Measured floors across BOTH palettes this button serves (TES-1990).
          The earlier note quoted only the --tls-c-* collection set:
            collection  rust  5.02:1 dark   teal  4.90:1 light
            category    animation 6.01:1 dark   memes 4.74:1 light  <- floor
          All 42 accent/theme pairs clear 4.5:1. Re-measure before retuning any
          --accent-category-* or --tls-c-* light value; they are two separately
          tuned palettes and the category set is the tighter one. */}
      <span className="hidden font-normal sm:inline">
        {isLoading && isPlaying
          ? "Loading…"
          : isPlaying && activeIndex !== null
            ? `${activeIndex + 1} / ${previewSounds.length}`
            : `First ${previewCount}`}
      </span>
    </button>
  )
}
